/**
 * WHICH PIECE OF PAPER A LINE PRINTS ON.
 *
 * BAR wants towing and storage disclosed and authorized on a document
 * separate from the repair estimate and the repair invoice. See migration
 * 0118 for why, including the one with money on it: storage rides on the
 * Civil Code 3068 possessory lien, which is a different instrument from
 * the repair contract, and job 1675 has $2,730 of it.
 *
 * ── THIS IS NOT `kind` ──────────────────────────────────────────────
 *
 * `kind` is a payroll instruction. commission.ts counts labour and parts;
 * fee is inert. Storage is correctly kind='fee' and stays that way.
 * `bill_as` answers a different question and the two must not be collapsed
 * into one column, because a shop that wanted storage to earn commission
 * could then only get it by putting it on the repair invoice.
 *
 * Pure, and shared with the server the way trade.ts and partcondition.ts
 * are, because the invoice email says these words too.
 */

export const BILL_AS = ["repair", "storage", "tow"] as const;
export type BillAs = (typeof BILL_AS)[number];

type Paper = {
  /** What an advisor picks in the editor. */
  option: string;
  /** The heading on the document itself. */
  title: string;
  /** invoices.kind for this paper. Repair keeps the name it has always had. */
  invoiceKind: "final" | "storage" | "tow";
  /**
   * The suffix on the invoice number. There is a unique index on
   * (account_id, invoice_number), so a job that raises both a repair bill
   * and a storage bill needs two different numbers or the second insert
   * fails with a constraint error nobody can read.
   */
  suffix: string;
  /** Said when this paper has nothing on it, in words rather than "0 rows". */
  nothingOnIt: string;
};

const PAPER: Record<BillAs, Paper> = {
  repair: {
    option: "On the repair bill",
    title: "Invoice",
    invoiceKind: "final",
    suffix: "-I",
    nothingOnIt: "Nothing is priced on this job, so there is nothing to bill.",
  },
  storage: {
    option: "On a storage bill, separately",
    title: "Storage",
    invoiceKind: "storage",
    suffix: "-S",
    nothingOnIt: "There is no storage on this job. Add the days first.",
  },
  tow: {
    option: "On a towing bill, separately",
    title: "Towing",
    invoiceKind: "tow",
    suffix: "-T",
    nothingOnIt: "There is no towing on this job. Add it first.",
  },
};

export function asBillAs(v: unknown): BillAs {
  const s = String(v ?? "").trim().toLowerCase();
  // Anything unrecognised is repair, which is where a line billed today
  // already prints. An unknown value must never quietly move money onto a
  // document the customer never agreed to.
  return (BILL_AS as readonly string[]).includes(s) ? (s as BillAs) : "repair";
}

export const billAsOption = (v: unknown): string => PAPER[asBillAs(v)].option;
export const billAsTitle = (v: unknown): string => PAPER[asBillAs(v)].title;
export const invoiceKindFor = (v: unknown) => PAPER[asBillAs(v)].invoiceKind;
export const numberSuffixFor = (v: unknown): string => PAPER[asBillAs(v)].suffix;
export const nothingOnIt = (v: unknown): string => PAPER[asBillAs(v)].nothingOnIt;

/** Options for a <select>, repair first because it is almost every line. */
export function billAsOptions(): Array<{ value: BillAs; text: string }> {
  return BILL_AS.map((v) => ({ value: v, text: PAPER[v].option }));
}

/**
 * Back from an invoice to the lines it should print.
 *
 * A deposit is a part payment toward the repair bill, so it reads the
 * repair lines. Anything unrecognised reads repair too, which is where
 * every invoice raised before 0118 belongs.
 */
export function paperForInvoiceKind(kind: unknown): BillAs {
  const k = String(kind ?? "").trim().toLowerCase();
  if (k === "storage") return "storage";
  if (k === "tow") return "tow";
  return "repair";
}

/** The invoice kinds that are NOT the repair bill. */
export const SEPARATE_KINDS = ["storage", "tow"] as const;

/**
 * Split a job's lines by which paper they belong on.
 *
 * Used by raiseInvoice to total one document, and by the invoice email to
 * print one. Both have to agree or a customer gets a bill whose lines do
 * not add up to its total, which is the complaint that ends in a chargeback.
 */
export function linesFor<T extends { bill_as?: unknown }>(
  lines: readonly T[],
  paper: BillAs,
): T[] {
  return lines.filter((l) => asBillAs(l.bill_as) === paper);
}

/**
 * Which separate papers this job actually needs, so the desk can offer
 * exactly those buttons and no empty ones.
 */
export function separatePapersOn<T extends { bill_as?: unknown; total?: unknown }>(
  lines: readonly T[],
): BillAs[] {
  return SEPARATE_KINDS.filter((p) =>
    linesFor(lines, p).some((l) => Number(l.total ?? 0) !== 0));
}

/**
 * WHICH SEPARATE PAPERS HAVE MONEY ON THEM AND NO YES BEHIND THEM.
 *
 * BAR wants towing authorized on its own form, separately from the repair
 * authorization. Splitting the document while one signature still covers
 * everything is a filing change dressed up as consent, so something has to
 * notice when the second signature was never taken.
 *
 * IT REPORTS, IT DOES NOT BLOCK. Same rule as the part condition nag. A
 * missing authorization is a thing to go and get, not a reason to leave a
 * shop unable to bill a car sitting in its yard. Refusing here would also
 * punish the honest case, which is a tow agreed on the phone at 11pm and
 * written up the next morning.
 *
 * A DECLINED authorization does not count as one. Somebody saying no to a
 * tow is the opposite of consent for it.
 */
export function papersNeedingConsent<
  L extends { bill_as?: unknown; total?: unknown },
  A extends { authorizes?: unknown; declined?: unknown },
>(lines: readonly L[], auths: readonly A[]): BillAs[] {
  const agreed = new Set(
    auths.filter((a) => !a.declined).map((a) => asBillAs(a.authorizes)),
  );
  return separatePapersOn(lines).filter((p) => !agreed.has(p));
}

/** Said to an advisor about a paper with no yes behind it. */
export function consentWanted(paper: BillAs): string {
  return paper === "tow"
    ? "Nobody has agreed to the towing yet. That is agreed on its own, separately from the repair."
    : "Nobody has agreed to the storage yet. Check the terms they signed cover it before billing.";
}
